// サーバーAPI（apps/worker）のクライアント。エンドポイントの一覧はここに集める（Documentation/ARCHITECTURE.md の web 節）。
// 育成系の操作は応答の形に依存せず、成功したら /api/me を取り直して state を正本に合わせる（state.ts の act）。
import './error-log'; // 例外の収集は API より先に（起動直後の例外も拾う）
import type { AutoSell, BattleResult, BattleSetup, BuildingType, CapacityInfo, CharId, Command, EquipItem, GachaItem, OnceGrant, Profile, Rewards, SellFilter, SurveyAnswers, SurveyDef } from '@hyakki/core';

export type ResourceKind = 'zeni' | 'reidama' | 'zaimoku' | 'exp' | 'hyoro' | 'fuda' | 'kuji' | 'soutou';
export type Resources = Partial<Record<ResourceKind, number>>;
export type Grant = { kind: ResourceKind; amount: number } | { kind: 'item'; id: string; amount: number };

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public body: unknown = null,
  ) {
    super(`${status} ${code}`);
  }
  get notReady() {
    return this.status === 404 || this.status === 501;
  }
  get offline() {
    return this.status === 0;
  }
}

// ---------- 開発ログイン（import.meta.env.DEV のときだけ） ----------
const DEV_KEY = 'hk.devUser';
const REQUEST_TIMEOUT_MS = 15000;
export const devAuth = {
  get(): string | null {
    if (!import.meta.env.DEV) return null;
    try {
      return localStorage.getItem(DEV_KEY);
    } catch {
      return null;
    }
  },
  set(id: string | null) {
    if (!import.meta.env.DEV) return;
    try {
      if (id) localStorage.setItem(DEV_KEY, id);
      else localStorage.removeItem(DEV_KEY);
    } catch {
      /* 保存できなくても続行 */
    }
    // WebSocket はヘッダーを付けられないので、Vite のプロキシがこの Cookie を x-dev-user に載せ替える（vite.config.ts）
    document.cookie = id ? `hk_dev=${encodeURIComponent(id)}; path=/; SameSite=Strict` : 'hk_dev=; path=/; max-age=0';
  },
};

type Listener = (e: ApiError) => void;
const errorListeners = new Set<Listener>();
/** 401（ログイン切れ）・503（メンテナンス）・通信断を画面全体で拾う窓口 */
export const onApiError = (fn: Listener) => (errorListeners.add(fn), () => errorListeners.delete(fn));

async function request<T>(method: string, path: string, body?: unknown, quiet = false, timeoutMs = REQUEST_TIMEOUT_MS): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  const dev = devAuth.get();
  if (dev) headers['x-dev-user'] = dev;
  let res: Response;
  // 応答が返らないまま固まらないよう時間切れを付ける（AbortSignal.timeout は古い Safari に無いので自前）
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    res = await fetch(path, { method, headers, credentials: 'same-origin', body: body === undefined ? undefined : JSON.stringify(body), signal: ctrl.signal });
  } catch {
    clearTimeout(timer);
    const e = new ApiError(0, 'network');
    if (!quiet) errorListeners.forEach((f) => f(e));
    throw e;
  }
  clearTimeout(timer);
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if (!res.ok) {
    const code = String((data && typeof data === 'object' && 'error' in data && (data as { error: unknown }).error) || res.statusText || 'error');
    const e = new ApiError(res.status, code, data);
    if (!quiet) errorListeners.forEach((f) => f(e));
    throw e;
  }
  return data as T;
}

const get = <T>(p: string, quiet = false) => request<T>('GET', p, undefined, quiet);
const post = <T>(p: string, b: unknown = {}) => request<T>('POST', p, b);
const del = <T>(p: string) => request<T>('DELETE', p);
/** mutate 系の応答は { response } に包まれていることがある */
const unwrap = <T>(r: unknown): T => (r && typeof r === 'object' && 'response' in r && (r as { response: unknown }).response != null ? (r as { response: T }).response : (r as T));
const postU = async <T>(p: string, b: unknown = {}) => unwrap<T>(await post<unknown>(p, b));

// ---------- 型（worker の応答） ----------
export interface StaminaView {
  value: number;
  reserve: number;
  max: number;
  reserveMax: number;
  nextAt: number | null;
}
export interface StageRow {
  stageId: number;
  difficulty: number;
  stars: number; // ビット：1=勝利 2=戦闘不能なし 4=龍脈
  clears: number;
}
export interface MeResponse {
  serverTime: number;
  /** JST の日付（YYYY-MM-DD。無料ガチャ・任務の区切り） */
  day?: string;
  user: { id: string; displayName: string; needsName: boolean; createdAt: number; guest?: boolean };
  profile: Profile;
  version: number;
  level: { level: number; into: number; need: number; total?: number; nextSkillUnlock?: number | null };
  /** お供（仲間の式神から1体。能力の説明は★の値入り） */
  companion?: {
    enemyId: string;
    name: string;
    star: number;
    level: number;
    ability: string;
    abilityName: string;
    desc: string;
    second: { name: string; desc: string } | null;
    ougi: { name: string; when: string; desc: string } | null;
  } | null;
  /** 解放力（内訳）と4人の装着状況 */
  skills?: { capacity: CapacityInfo; presetSlots: number; chars: Record<CharId, SkillViewData> };
  resources: Resources;
  stamina: StaminaView;
  items: Record<string, number>;
  entitlements: string[];
  stages: StageRow[];
  rank: { maxStage: number; starsTotal: number; donated: number; provinces?: number };
  /** 国盗り：10国・20地方の状態（Profile からも core の provinceViews で計算できる） */
  worldmap?: { countries: { id: string; name: string; regions: string[]; commander: string; conquered: boolean; cleared: number; districts: number }[]; districtsTotal: number; provinces: { id: string; state: 'conquered' | 'open' | 'gated' | 'fog' | 'frontier'; cleared: number; stars: number }[]; conquered: number; total: number; nextStage: number | null };
  activeBattle: { id: string; stageId: number; difficulty: number; charId: CharId; kind: string; createdAt: number } | null;
  unreadPresents: number;
  /** 開いているアンケート。answered＝答えた（お知らせ・TOP の印を消す） */
  survey?: { id: string; title: string; reward: Grant[]; answered: boolean } | null;
  buffs: { mult: number; until: number; name?: string }[];
  loginBonus: { claimedToday: boolean; days: number; next: Grant[] | null; cycle: unknown };
}
/** キャラ1人分のスキル構成（/api/skills/*） */
export interface SkillViewData {
  charId: CharId;
  auto: boolean;
  equipped: string[];
  used: number;
  capacity: number;
  presets: (string[] | null)[];
  /** 3つの術（覚えたか・持っていくか・覚えるスキル） */
  jutsu?: JutsuView[];
}
export interface JutsuView {
  id: string;
  name: string;
  desc: string;
  ougiDesc: string;
  kind: string;
  learned: boolean;
  selected: boolean;
  skill: string | null;
}
export interface StartResponse {
  sessionId: string;
  setup: BattleSetup;
  hyoroCost?: number;
  /** 本日初出撃の倍率 */
  dropMult?: number;
  /** ドロップ2倍の権利（drop2x）の倍率。持っていれば 2 */
  dropBoost?: number;
  stamina?: StaminaView;
}
/** 売った数と銭（自動売却・所持枠超過） */
export interface SoldSummary {
  count: number;
  zeni: number;
}
/** 報酬を Profile に反映した結果（core の AppliedRewards） */
export interface AppliedView {
  levelUps?: number;
  overflowReidama?: number;
  /** 自動売却＋所持枠超過の銭の合計（旧名） */
  dismantledZeni: number;
  /** 所持品に入った装備（追加効果つき） */
  items: EquipItem[];
  autoSold: SoldSummary;
  overflowSold: SoldSummary;
  /** 拾ったお守り（所持品に入った id、上限を超えて御朱印になった数） */
  omamori?: { ids: number[]; goshuin: number; reidama?: number };
  /** お供がいなかったので自動でお供にした式神（その敵ID） */
  companionAuto?: string;
}
export interface FinishResponse {
  result: BattleResult;
  rewards?: Partial<Rewards> & { grants?: Grant[] };
  firstClear?: boolean;
  refunded?: number;
  /** 一度きりの報酬（星・図鑑・ボス初撃破など）。bundle は資源、omamori はお守りID */
  oneTime?: { source: string; bundle?: Resources; omamori?: (string | { defId: string; rarity: string })[]; grants?: Grant[] }[];
  levelBefore?: number;
  /** 戦闘後の主Lv（数値。古い形は { level }） */
  level?: number | { level: number; into: number; need: number };
  /** 反映結果（入った装備・自動売却・所持枠超過） */
  applied?: AppliedView;
  already?: boolean;
}
export interface SweepRun {
  stageId: number;
  win: boolean;
  stars: [boolean, boolean, boolean];
  firstClear: boolean;
  rewards: Rewards;
  applied: AppliedView;
  oneTime: OnceGrant[];
}
export interface SweepResponse {
  sessionId: string;
  runs: SweepRun[];
  hyoroSpent: number;
  /** 使った掃討チケット */
  ticketsSpent: number;
  zeniFee: number;
  dropBoost: number;
  /** 所持品に入った装備の数（全回の合計） */
  itemsGained: number;
  autoSold: SoldSummary;
  overflowSold: SoldSummary;
  level?: number;
  stamina?: StaminaView;
}
/** 閉じていたあいだの周回（runs の rewards・applied は掃討と同じ形） */
export interface FarmSettleResponse {
  runs: Pick<SweepRun, 'stageId' | 'win' | 'stars' | 'rewards' | 'applied' | 'oneTime'>[];
  battles: number;
  minutes: number;
  stageId?: number;
  charId?: CharId;
  level?: number;
  stamina?: StaminaView;
}
export interface SellResult {
  zeni: number;
  count: number;
  itemIds: number[];
}
export interface Present {
  key: string;
  title: string;
  body: string;
  grants: Grant[];
  createdAt: number;
  expiresAt: number | null;
}
export interface ShopProduct {
  id: string;
  name: string;
  yen: number;
  type: 'permanent' | 'consumable' | 'cosmetic' | 'pass';
  /** 月パス：残りが10日以上／所持枠：上限に届いた。今は買えない */
  full?: boolean;
  /** 所持枠+50：今の枠・上限・買った回数 */
  slots?: { cap: number; max: number; bought: number };
  desc: string;
  owned?: boolean;
  remainingToday?: number | null;
}
export interface Order {
  id: string;
  productId: string;
  name: string;
  amount: number;
  status: 'pending' | 'paid' | 'expired' | 'refunded' | 'disputed' | string;
  createdAt: number;
  paidAt?: number | null;
  fulfilledAt?: number | null;
  /** 課金の10連（おまもりガチャ10連・月パス）で引いた中身 */
  items?: GachaItem[];
}
export interface ChatMessage {
  id: string;
  userId: string | null;
  name: string;
  text: string;
  at: number;
  kind?: 'user' | 'system' | 'kishin';
  /** 発言者のアイコン（図鑑の妖の id） */
  avatar?: string;
  /** アイコン縁（月パス） */
  frame?: string;
}
export interface Announcement {
  id: string;
  title: string;
  body: string;
  startsAt: number;
  endsAt: number | null;
}
export interface ServerStatus {
  serverTime: number;
  /** テストサーバー・開発環境（DEV_AUTH=1 かつ TEST_PURCHASE=1）。本番では false */
  testMode?: boolean;
  maintenance: { on: boolean; message: string } | null;
  announcements: Announcement[];
  /** 共同国盗り（週替わり・自動）。total/goal は今週の全員の奉納（銭） */
  event?: {
    state: 'open' | 'soon' | 'none';
    kind?: string;
    title?: string;
    week?: string;
    until?: number | null;
    progress?: number;
    total?: number;
    goal?: number;
    donors?: number;
    provinces?: { taken: number; total: number };
    rules?: string[];
  } | null;
}
export interface RankRow {
  rank: number;
  userId: string;
  name: string;
  maxStage: number;
  /** 制圧した国の数（国盗りのランキング） */
  provinces?: number;
  stars: number;
  level: number;
  donated: number;
  /** 自分で選んだ主人公の姿（未設定なら null） */
  avatar?: string | null;
}
export interface MissionView {
  id: string;
  key?: string;
  text?: string;
  title?: string;
  name?: string;
  desc?: string;
  target: number;
  progress: number;
  grants?: Grant[];
  reward?: Resources;
  claimed: boolean;
}

// ---------- 呼び出し ----------
/** 離脱の計測：その段階に着いたことを送る（同じ段階は1回の起動で1回。失敗しても知らせない） */
const tracked = new Set<string>();
export function track(step: 'prologue.intro' | 'prologue.battle' | 'prologue.ashigaru' | 'prologue.general' | 'prologue.win' | 'prologue.done' | 'stage1.start') {
  if (tracked.has(step)) return;
  tracked.add(step);
  void request('POST', '/api/funnel', { step }, true).catch(() => tracked.delete(step));
}

export const api = {
  // 認証・アカウント
  authConfig: () => get<{ googleClientId: string }>('/api/auth/config', true),
  authNonce: () => get<{ nonce: string }>('/api/auth/nonce', true),
  /** source：流入元（新しくアカウントを作ったときだけサーバーが保存する。ui/source.ts） */
  googleLogin: (credential: string, source?: unknown) => post<{ ok: true; created: boolean; needsName: boolean }>('/api/auth/google', { credential, source }),
  guestLogin: (source?: unknown) => post<{ ok: true; created: boolean; needsName: boolean }>('/api/auth/guest', { source }),
  logout: () => post<{ ok: true }>('/api/auth/logout'),
  /** この端末をいま遊んでいる端末にする（ほかの端末は replaced で閉じる） */
  claim: () => request<{ ok: boolean }>('POST', '/api/auth/claim', {}, true),
  linkGoogle: (credential: string) => post<{ ok: true }>('/api/account/link-google', { credential }),
  me: (quiet = false) => get<MeResponse>('/api/me', quiet),
  setName: (name: string, avatar?: string) => post<{ ok: true; name: string }>('/api/account/name', { name, avatar }),
  rename: (name: string, nonce: string) => post<{ ok: true; name: string; already?: boolean }>('/api/account/rename', { name, nonce }),
  deleteAccount: () => post<{ ok: true }>('/api/account/delete', { confirm: 'DELETE' }),
  setOshi: (charId: CharId) => postU<{ oshi: CharId }>('/api/oshi', { charId }),
  /** 戦闘に持ち出す道具（1〜2種類・解放済みだけ） */
  setItems: (ids: string[], charId?: string) => post<{ battleItems: string[] }>('/api/items', { ids, charId }),
  /** チュートリアルの案内を見終えた（reset で全部やり直し） */
  tutorial: (seen: string[], reset = false) => post<{ tutorial: string[] }>('/api/tutorial', reset ? { reset: true } : { seen }),
  /** お供（仲間の式神の敵ID。null で外す）。出撃キャラと同じく1つの設定 */
  setCompanion: (enemyId: string | null, charId?: string) => post<{ companion: { enemyId: string; star: number; level: number; ability: string } | null }>('/api/shikigami/companion', { enemyId, charId }),
  /** 霊玉で式神Lvを上げる（levels 段か toMax で上限まで）。from は画面が見ている式神Lv（違えば 409）、nonce で再送しても一度きり */
  levelShikigami: (b: { enemyId: string; levels?: number; toMax?: boolean; from: number; nonce: string }) =>
    post<{ enemyId: string; from: number; to: number; cost: number; cap: number; level: number; reidama: number; already?: boolean }>('/api/shikigami/level', b),
  status: () => get<ServerStatus>('/api/status', true),
  ping: () => post<{ ok: true }>('/api/ping'),
  claimLoginBonus: () => postU<{ day: number; grants: Grant[] }>('/api/login-bonus/claim'),

  // 戦闘
  /** opId：1回の出陣ごとの操作ID。通信が切れて送り直しても同じ戦闘が返る（兵糧は1回分） */
  battleStart: (stageId: number, difficulty: number, charId: CharId, auto = false, opId?: string, live = false) =>
    postU<StartResponse>('/api/battle/start', { stageId, difficulty, charId, auto, opId, ...(live ? { live } : {}) }),
  battleActive: () => get<{ session: { sessionId: string; kind: string; setup: BattleSetup; hyoroCost: number; createdAt: number } | null }>('/api/battle/active'),
  battleFinish: (sessionId: string, commands: Command[]) => post<FinishResponse>('/api/battle/finish', { sessionId, commands }),
  /** 節電モードの1戦を盤面を見る1戦に（最低時間の待ちを外す） */
  battleLive: (sessionId: string) => postU<{ ok: true; changed: boolean }>('/api/battle/live', { sessionId }),
  battleAbandon: (sessionId: string) => postU<{ ok: true }>('/api/battle/abandon', { sessionId }),
  /** オート周回：閉じていたあいだ（最後の1戦から10分まで）の周回をサーバーが計算して受け取る。周回は止まる */
  farmSettle: () => postU<FarmSettleResponse>('/api/farm/settle'),
  /** オート周回を止めた（猶予を付けない） */
  farmStop: () => postU<{ ok: true }>('/api/farm/stop'),
  farmAlive: () => postU<{ ok: true }>('/api/farm/alive'),
  /** プッシュ通知：公開鍵（未設定のサーバーは null）・この端末の登録（種類）・解除 */
  pushKey: () => get<{ key: string | null }>('/api/push', true),
  pushOn: (subscription: PushSubscriptionJSON, kinds: string[]) => post<{ ok: true; kinds: string[] }>('/api/push', { subscription, kinds }),
  pushOff: (endpoint: string) => post<{ ok: true }>('/api/push/off', { endpoint }),
  pushState: (endpoint: string) => post<{ kinds: string[] | null }>('/api/push/state', { endpoint }),
  /** opId：送り直しで二重に掃討しないための操作ID（同じIDの2回目は already_done） */
  battleSweep: (stageId: number, difficulty: number, count: number, charId: CharId, opId?: string) =>
    post<SweepResponse>('/api/battle/sweep', { stageId, difficulty, count, charId, opId }),
  /** 国の一括掃討：未クリアの合戦（掃討できるもの）を1つずつ勝つまで（各 tries 回まで）。本城は対象外 */
  battleSweepProvince: (provinceId: string, charId: CharId, tries = 2, opId?: string) =>
    post<SweepResponse & { provinceId: string; targets: number[]; cleared: number[] }>('/api/battle/sweep-province', { provinceId, charId, tries, opId }),

  // 育成（成功後は refreshMe で正本を取り直す）
  /** スキル：構成を決める（手動）／おすすめ構成（auto）／保存枠（build_slots） */
  skillsSet: (charId: CharId, skillIds: string[]) => post<SkillViewData>('/api/skills/set', { charId, skillIds }),
  skillsAuto: (charId: CharId) => post<SkillViewData>('/api/skills/auto', { charId }),
  /** 持っていく術を選ぶ（覚えている術だけ） */
  skillsJutsu: (charId: CharId, jutsu: string) => post<SkillViewData>('/api/skills/jutsu', { charId, jutsu }),
  skillsPresetSave: (charId: CharId, slot: number) => post<SkillViewData>('/api/skills/presets/save', { charId, slot }),
  skillsPresetLoad: (charId: CharId, slot: number) => post<SkillViewData>('/api/skills/presets/load', { charId, slot }),
  equip: (charId: CharId, slot: 'weapon' | 'armor' | 'netsuke', uid: number | null) => post<unknown>('/api/equipment/equip', { charId, slot, itemId: uid }),
  enhance: (uid: number, count = 1) => post<{ level: number; cost: number; count: number }>('/api/equipment/enhance', { itemId: uid, count }),
  /** 合成（限界突破）：同じ部位・同じ段以上の装備を素材に、強化の上限を+10 */
  limitBreak: (uid: number, fodderId: number) => post<{ lb: number; cost: number }>('/api/equipment/limit-break', { itemId: uid, fodderId }),
  /** おすすめ装備（戦力が最も高くなる組み合わせ）。apply=false なら見積もりだけ */
  /** おすすめビルド（型）でスキル・装備・お守り・道具・式神をまとめて組む（style='std' はおすすめ） */
  recommendBuild: (charId: CharId, style: string, includeOthers: boolean) => post<{ before: number; after: number; style: string }>('/api/recommend/build', { charId, style, includeOthers }),
  recommend: (charId: CharId, includeOthers: boolean, apply: boolean) =>
    post<{ recommendation: { set: Record<'weapon' | 'armor' | 'netsuke', number | null>; before: number; after: number; takenFrom: { itemId: number; charId: CharId }[] }; sets?: unknown }>(
      '/api/equipment/recommend',
      { charId, includeOthers, apply },
    ),
  lock: (uid: number, locked: boolean) => post<{ item?: EquipItem }>('/api/equipment/lock', { itemId: uid, locked }),
  /** 売却（銭のみ）。ロック中・装備中は飛ばされる */
  sell: (uids: number[]) => post<SellResult>('/api/equipment/sell', { itemIds: uids }),
  /** 一括売却の見積もり（Profile は変えない） */
  sellPreview: (filter: SellFilter) => post<SellResult>('/api/equipment/sell-preview', { filter }),
  sellFilter: (filter: SellFilter) => post<SellResult>('/api/equipment/sell-filter', { filter }),
  setAvatar: (avatar: string | null) => post<{ settings: Profile['settings'] }>('/api/settings/avatar', { avatar }),
  setFrame: (frame: string | null) => post<{ settings: Profile['settings'] }>('/api/settings/frame', { frame }),
  setAutoSell: (rarity: AutoSell, maxTier?: number | null) => post<{ settings: Profile['settings'] }>('/api/settings/auto-sell', { rarity, maxTier }),
  equipOmamori: (charId: CharId, slot: number, omamoriId: number | null) => post<{ sets: Profile['omamoriSets'] }>('/api/omamori/equip', { charId, slot, omamoriId }),
  upgradeBlueprint: (building: BuildingType) => post<unknown>('/api/blueprint/upgrade', { building }),
  collectVillage: () => postU<{ gain?: Grant[] | Resources; zeni?: number; reidama?: number; zaimoku?: number; exp?: number; hours?: number }>('/api/village/collect'),
  upgradeFacility: (facility: string) => post<unknown>('/api/village/upgrade', { facility }),
  /** 万屋：品物（good）か道具の解放（item）を1つ買う */
  exchangeKomedoiya: (times: number) => post<{ times: number; hyoro: number; tickets: number }>('/api/komedoiya/exchange', { times }),
  buyYorozuya: (target: { good: string } | { item: string }) => post<{ price: number }>('/api/yorozuya/buy', target),
  gacha: (count: 1 | 10) => postU<{ items?: unknown[]; pulls?: unknown[]; cost: number; pity?: number }>('/api/gacha', { count }),
  /** 毎日1回の無料ガチャ（JST の日付。2回目は 409） */
  gachaFree: () => post<{ items: unknown[]; fortune: string; perk: unknown; lucky: boolean; cost: number }>('/api/gacha/free'),
  /** お守り：ロック・売却（霊玉。to は 'reidama'。古い画面の 'zeni' も霊玉で払う）・一括売却・錬成（nonce で一度きり） */
  lockOmamori: (omamoriId: number, locked: boolean) => post<{ locked: boolean }>('/api/omamori/lock', { omamoriId, locked }),
  sellOmamori: (omamoriIds: number[]) => post<{ count: number; reidama: number }>('/api/omamori/sell', { omamoriIds, to: 'reidama' }),
  sellOmamoriFilter: (maxRarity: string, preview = false) =>
    post<{ count: number; reidama?: number; value?: number; ids?: number[] }>('/api/omamori/sell-filter', { maxRarity, to: 'reidama', preview }),
  fuseOmamori: (omamoriIds: number[], nonce: string) => post<{ item: { id: number } | null; itemId?: number; already?: boolean }>('/api/omamori/fuse', { omamoriIds, nonce }),
  dex: () => get<{ entries: { id: string; no: number; name: string | null; kills: number; shikigami: number }[]; found: number; total: number }>('/api/dex'),
  missions: () => get<{ day: string; week?: string; daily?: MissionView[]; weekly?: MissionView[]; allClearClaimed?: boolean; missions?: MissionView[] }>('/api/missions'),
  claimMission: (id: string) => postU<{ grants: Grant[] }>(`/api/missions/${encodeURIComponent(id)}/claim`),
  hounou: (amount: number) => postU<{ donated: number }>('/api/hounou', { amount }),

  // 課金（Stripe Checkout）
  products: () => get<{ products: ShopProduct[]; monthly?: { until: number | null }; enabled: boolean; testPurchase?: boolean }>('/api/shop/products'),
  checkout: (productId: string) => post<{ url?: string; orderId: string; granted?: boolean; test?: boolean; result?: { items?: GachaItem[] } }>('/api/shop/checkout', { productId }),
  orders: () => get<{ orders: Order[] }>('/api/shop/orders'),
  refreshOrder: (id: string) => post<{ order: Order }>(`/api/shop/orders/${encodeURIComponent(id)}/refresh`),

  // アンケート
  survey: () => get<{ survey: (SurveyDef & { endsAt: number | null }) | null; answers?: SurveyAnswers | null }>('/api/survey'),
  answerSurvey: (id: string, answers: SurveyAnswers) => post<{ ok: true; granted: Grant[] | null }>(`/api/survey/${encodeURIComponent(id)}`, { answers }),

  // プレゼント
  presents: () => get<{ presents: Present[] }>('/api/presents'),
  claimPresent: (key: string) => post<{ status: string; grants?: Grant[] }>(`/api/presents/${encodeURIComponent(key)}/claim`),
  claimAll: () => post<{ claimed: string[]; grants: Grant[] }>('/api/presents/claim-all'),

  // ランキング
  /** ステージごとの最速手番（上位50と自分） */
  stageRanking: (stageId: number, diff = 0) =>
    get<{ top: { rank: number; userId: string; name: string; turns: number; power: number; charId: CharId; avatar: string | null }[]; me: { rank: number; turns: number; power: number } | null; updatedAt?: number; cacheSec?: number }>(`/api/ranking/stage?stage=${stageId}&diff=${diff}`),
  ranking: (type: 'stage' | 'level' | 'donation' = 'stage') =>
    get<{ type: string; top: RankRow[]; me: { rank: number | null; maxStage: number; provinces?: number; stars: number; level: number; donated: number } | null; updatedAt?: number; cacheSec?: number }>(`/api/ranking?type=${type}`),

  // チャット（受信は WebSocket、送信は HTTP）
  chatHistory: () => get<{ messages: ChatMessage[] }>('/api/chat/messages', true),
  /** report を付けると運営だけへの報告（応答に message は無い） */
  /** 要望・不具合報告（運営だけが読む。images は data:image/jpeg;base64 の3枚まで） */
  /** 自分の実績を全体チャットに流さない（サーバーの設定） */
  setFeats: (hide: boolean) => post<{ ok: true; hideFeats: boolean }>('/api/account/feats', { hide }),
  feedbackSend: (kind: 'contact' | 'request' | 'bug', text: string, images: string[], context: Record<string, unknown>) =>
    request<{ report: { id: string } }>('POST', '/api/feedback', { kind, text, images, context }, false, 60_000),
  feedbackList: () => get<{ reports: { id: string; kind: 'contact' | 'request' | 'bug'; text: string; images: number; createdAt: number }[] }>('/api/feedback'),
  chatSend: (text: string, nonce: string, report?: Record<string, unknown>) => post<{ message?: ChatMessage; report?: { id: string } }>('/api/chat/messages', report ? { text, nonce, report: true, context: report } : { text, nonce }),
  report: (messageId: string, reason: string) => post<{ ok: true; hidden: boolean }>(`/api/chat/messages/${encodeURIComponent(messageId)}/report`, { reason }),
  blocks: () => get<{ blocks: { userId: string; name: string | null }[] }>('/api/chat/blocks'),
  block: (userId: string) => post<{ ok: true }>('/api/chat/blocks', { userId, kind: 'block' }),
  unblock: (userId: string) => del<{ ok: true }>(`/api/chat/blocks/${encodeURIComponent(userId)}`),
};

// ---------- 茶屋のたまき（AIと話す・遊び方の相談。worker の support/routes.ts） ----------
export type SupportCategory = 'howto' | 'bug' | 'purchase' | 'account' | 'abuse' | 'other';
export type SupportStatus = 'ai' | 'open' | 'answered' | 'closed';
export type SupportFace = 'normal' | 'smile' | 'sorry' | 'think';
export interface SupportThread { id: string; category: SupportCategory; status: SupportStatus; title: string; createdAt: number; updatedAt: number; unread: boolean }
/** action：contact＝たまきが「お問い合わせ・要望・報告」を案内した */
export interface SupportMessage { id: string; role: 'user' | 'ai' | 'staff' | 'system'; text: string; at: number; face?: SupportFace; action?: 'contact' }
/** freeLeft・freeMax：Google 連携の無料（最初の10回・補充なし。ゲストは0）。freePaused：今日の無料の分は終わった（チケットなら話せる） */
export interface SupportQuota { on: boolean; freePaused?: boolean; freeLeft: number; freeMax: number; guest: boolean; tickets: number }
export type SupportThreadOut = { thread: SupportThread; messages: SupportMessage[]; ai: SupportQuota };
/** AIの返事を待つので長めに待つ */
const SUPPORT_TIMEOUT_MS = 70_000;
export const supportApi = {
  list: () => get<{ name: string; threads: SupportThread[]; unread: number; ai: SupportQuota }>('/api/support', true),
  thread: (id: string) => get<{ thread: SupportThread; messages: SupportMessage[] }>(`/api/support/threads/${encodeURIComponent(id)}`),
  start: (text: string, nonce: string, context: Record<string, unknown>) => request<SupportThreadOut>('POST', '/api/support/threads', { text, nonce, context }, false, SUPPORT_TIMEOUT_MS),
  say: (id: string, text: string, nonce: string) => request<SupportThreadOut>('POST', `/api/support/threads/${encodeURIComponent(id)}/messages`, { text, nonce }, false, SUPPORT_TIMEOUT_MS),
};

export function chatSocketUrl() {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/api/chat/ws`;
}
